// Cleaned transcript renderer with turn-level audio scroll-along.
//
// The scrubber rewrites text, so word-level alignment isn't reliable —
// the back-end only emits per-turn {role, start_ms, end_ms} as
// `data.turns_meta`. When present, we pair each emitted turn (parsed
// from `data.text`) with the corresponding `turns_meta` entry by
// position and scroll the active turn into view as `currentTimeMs`
// crosses turn boundaries.
//
// When `turns_meta` is absent (legacy data), we fall through to the
// parent's legacy text renderer.

import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Text,
  Avatar,
  Badge,
  Divider,
} from "@chakra-ui/react";

// Dashed separator + "Source #N" badge between turns coming from
// different source TAGGED detail items. CLEANED data doesn't carry
// per-word snippet ids — `source_tagged_detail_item_id` is the closest
// signal we have for grouping.
export const SourceMarker = ({ sourceId, sourceIndex }) => (
  <HStack
    spacing={2}
    align="center"
    data-testid="cleaned-source-marker"
    data-source-id={sourceId ?? ""}
  >
    <Divider borderStyle="dashed" />
    <Badge variant="subtle" colorScheme="gray" fontSize="xx-small" flexShrink={0}>
      Source #{sourceIndex + 1}
    </Badge>
    <Divider borderStyle="dashed" />
  </HStack>
);

const ROLE_COLOR = {
  Customer: "purple",
  Employee: "blue",
  Other: "gray",
};

const SCROLL_PAUSE_AFTER_USER_INTERACTION_MS = 5000;

const SPEAKER_LABEL_RE = /^\s*([A-Za-z][A-Za-z ]*?(?:\s*\([A-Za-z]+\))?)\s*:\s*"?(.*?)\s*$/;

function formatTimestamp(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "0:00";
  const total = Math.floor(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function normalizeRole(rawSpeaker) {
  const s = String(rawSpeaker || "").trim();
  if (/^customer/i.test(s)) return "Customer";
  if (/^employee/i.test(s)) return "Employee";
  return "Other";
}

// Parse the CLEANED `data.text` into one entry per turn. Mirrors the
// existing parser in ConversationDetailPage but emits role-normalized
// objects so they can pair with `turns_meta`.
function parseTextTurns(text) {
  if (typeof text !== "string") return [];
  const lines = text.split(/\r?\n/);
  const turns = [];
  let current = null;
  for (const line of lines) {
    const m = line.match(SPEAKER_LABEL_RE);
    if (m && m[1] && m[1].length <= 40) {
      if (current) turns.push(current);
      current = {
        speaker: m[1].trim(),
        role: normalizeRole(m[1]),
        text: m[2] || "",
      };
    } else if (current) {
      const cont = line.trim();
      if (cont) current.text = current.text ? `${current.text} ${cont}` : cont;
    } else if (line.trim()) {
      current = {
        speaker: null,
        role: "Other",
        text: line.trim(),
      };
    }
  }
  if (current) turns.push(current);
  for (const t of turns) {
    t.text = t.text.replace(/^"+/, "").replace(/"+\s*$/, "").trim();
  }
  return turns;
}

// Find which turn (by index) `currentTimeMs` belongs to. Returns -1
// when the cursor is before the first turn.
//
// `epochOffset` is subtracted before comparison — the scrubber inherits
// epoch ms from the source TAGGED turns which inherits from RAW, but the
// player's `currentTimeMs` is conv-relative. See manifest's
// `audio_start_epoch_ms`.
//
// STICKY BRACKETING: "the last turn whose start_ms <= currentTimeMs."
// Strict bracketing on `[start_ms, end_ms]` flickered off whenever a
// tick landed in an inter-turn gap. Sticky bracketing keeps the
// current turn highlighted right up until the NEXT turn starts.
function findActiveTurnIdx(turnsMeta, currentTimeMs, epochOffset = 0) {
  if (!Array.isArray(turnsMeta) || !Number.isFinite(currentTimeMs)) return -1;
  const off = Number(epochOffset) || 0;
  let best = -1;
  for (let i = 0; i < turnsMeta.length; i += 1) {
    const start = Number(turnsMeta[i]?.start_ms);
    if (!Number.isFinite(start)) continue;
    if (currentTimeMs < start - off) break;
    best = i;
  }
  return best;
}

export const CleanedTurn = ({ turn, meta, turnIdx, isActive, turnRef, epochOffset = 0, onSeekToMs }) => {
  const role = turn?.role || "Other";
  const color = ROLE_COLOR[role] || "gray";
  const startMs = Math.max(0, (Number(meta?.start_ms) || 0) - epochOffset);
  const canSeek = typeof onSeekToMs === "function" && Number.isFinite(Number(meta?.start_ms));
  const handleClick = canSeek ? () => onSeekToMs(startMs) : undefined;
  const handleKeyDown = canSeek
    ? (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSeekToMs(startMs);
        }
      }
    : undefined;
  return (
    <Flex
      ref={turnRef}
      gap={3}
      align="flex-start"
      data-testid="cleaned-turn"
      data-turn-idx={turnIdx}
      data-active={isActive ? "true" : "false"}
      data-role={role}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={canSeek ? "button" : undefined}
      tabIndex={canSeek ? 0 : undefined}
      cursor={canSeek ? "pointer" : "default"}
      _hover={canSeek ? { bg: "blackAlpha.50" } : undefined}
      borderRadius="md"
      transition="background 120ms ease"
    >
      <Avatar
        name={role}
        size="sm"
        bg={`${color}.500`}
        color="white"
        flexShrink={0}
      />
      <Box flex={1} minW={0}>
        <Flex align="baseline" gap={2} mb={1}>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color={`${color}.600`}
            textTransform="uppercase"
            letterSpacing="wide"
          >
            {role}
          </Text>
          {meta && (
            <Text
              fontSize="xs"
              color="gray.400"
              fontFamily="mono"
              data-testid="cleaned-turn-timestamp"
            >
              {formatTimestamp(startMs)}
            </Text>
          )}
        </Flex>
        <Box
          bg={isActive ? "yellow.50" : "gray.50"}
          borderRadius="lg"
          borderLeftWidth="3px"
          borderLeftColor={`${color}.300`}
          px={4}
          py={2}
        >
          <Text fontSize="sm" lineHeight="1.6" whiteSpace="pre-wrap">
            {turn?.text}
          </Text>
        </Box>
      </Box>
    </Flex>
  );
};

// Flatten CLEANED detail_items into a single ordered turn stream paired
// with `turns_meta` + source-item grouping ids. Extracted from the view so
// the virtualized embed viewer (EmbedConversationViewer) can reuse the
// EXACT same flattening + sticky-bracketing sync logic without duplicating
// the parser. The turn/sentence is the unit downstream consumers virtualize.
//
// Returns: { allTurns, allMeta, allSourceIds, hasAnyMeta }.
export function flattenCleanedTurns(items) {
  const turns = [];
  const meta = [];
  const sourceIds = [];
  let any = false;
  if (Array.isArray(items)) {
    const sorted = [...items].sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0));
    for (const item of sorted) {
      const data = item?.data || {};
      const parsed = parseTextTurns(data?.text || "");
      const itemMeta = Array.isArray(data?.turns_meta) ? data.turns_meta : [];
      // CLEANED items keep a pointer back to the originating TAGGED
      // detail item — that's the "source" we group by.
      const sourceId =
        data?.source_tagged_detail_item_id ??
        item?.source_tagged_detail_item_id ??
        item?.id ??
        item?.index ??
        null;
      if (itemMeta.length > 0) any = true;
      for (let i = 0; i < parsed.length; i += 1) {
        turns.push(parsed[i]);
        meta.push(i < itemMeta.length ? itemMeta[i] : null);
        sourceIds.push(sourceId);
      }
    }
  }
  return {
    allTurns: turns,
    allMeta: meta,
    allSourceIds: sourceIds,
    hasAnyMeta: any,
  };
}

// React hook wrapper around `flattenCleanedTurns` + `findActiveTurnIdx`.
// Memoizes both so re-renders driven only by `currentTimeMs` don't re-parse
// the (potentially large) item list. Both the desktop view below and the
// virtualized embed viewer drive sentence-sync off this single source.
export function useCleanedTurns(items, currentTimeMs = 0, audioStartEpochMs = 0) {
  const flattened = useMemo(() => flattenCleanedTurns(items), [items]);
  const activeIdx = useMemo(
    () => findActiveTurnIdx(flattened.allMeta, currentTimeMs, audioStartEpochMs),
    [flattened.allMeta, currentTimeMs, audioStartEpochMs]
  );
  return { ...flattened, activeIdx };
}

const CleanedTranscriptView = ({
  items,
  currentTimeMs = 0,
  // Epoch ms anchor of conversation audio (from audio manifest's
  // `audio_start_epoch_ms`). Subtracted from turns_meta start_ms before
  // comparing to conv-relative currentTimeMs. Default 0 = no normalization.
  audioStartEpochMs = 0,
  onSeekToMs,
  legacyRenderer,
}) => {
  const scrollPausedUntilRef = useRef(0);
  const lastScrolledTurnRef = useRef(-1);
  const turnRefs = useRef([]);

  const { allTurns, allMeta, allSourceIds, hasAnyMeta, activeIdx } =
    useCleanedTurns(items, currentTimeMs, audioStartEpochMs);

  useEffect(() => {
    const onWheel = () => {
      scrollPausedUntilRef.current =
        Date.now() + SCROLL_PAUSE_AFTER_USER_INTERACTION_MS;
    };
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("touchmove", onWheel, { passive: true });
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("touchmove", onWheel);
    };
  }, []);

  useEffect(() => {
    if (activeIdx < 0) return;
    if (activeIdx === lastScrolledTurnRef.current) return;
    if (Date.now() < scrollPausedUntilRef.current) return;
    const el = turnRefs.current[activeIdx];
    if (el && typeof el.scrollIntoView === "function") {
      try {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      } catch {
        /* noop */
      }
    }
    lastScrolledTurnRef.current = activeIdx;
  }, [activeIdx]);

  if (!hasAnyMeta) {
    if (typeof legacyRenderer === "function") {
      const sorted = Array.isArray(items)
        ? [...items].sort((a, b) => (a?.index ?? 0) - (b?.index ?? 0))
        : [];
      return legacyRenderer(sorted);
    }
    return null;
  }

  // Source-marker bookkeeping — interleave a dashed separator + badge
  // when the source detail item changes between adjacent turns.
  let lastSourceId = undefined;
  const seenSources = [];

  return (
    <VStack
      align="stretch"
      spacing={4}
      data-testid="cleaned-transcript-structured"
    >
      {allTurns.map((turn, idx) => {
        const sid = allSourceIds[idx];
        const showMarker = sid != null && sid !== lastSourceId;
        if (showMarker) {
          seenSources.push(sid);
        }
        const sourceIndex = seenSources.length - 1;
        lastSourceId = sid;
        return (
          <React.Fragment key={idx}>
            {showMarker && (
              <SourceMarker sourceId={sid} sourceIndex={sourceIndex} />
            )}
            <CleanedTurn
              turn={turn}
              meta={allMeta[idx]}
              turnIdx={idx}
              isActive={idx === activeIdx}
              epochOffset={audioStartEpochMs}
              onSeekToMs={onSeekToMs}
              turnRef={(el) => {
                turnRefs.current[idx] = el;
              }}
            />
          </React.Fragment>
        );
      })}
    </VStack>
  );
};

export default CleanedTranscriptView;
export { findActiveTurnIdx, parseTextTurns };
